(() => {
  const connectedSliders = new Map();

  const connectSlider = (slider) => {
    if (connectedSliders.has(slider)) return;

    const track = slider.querySelector('[data-cart-drawer-upsell-track]');
    const previousButton = slider.querySelector('[data-cart-drawer-upsell-prev]');
    const nextButton = slider.querySelector('[data-cart-drawer-upsell-next]');
    const list = slider.querySelector('#cart-upsell-drawer');

    if (!track || !previousButton || !nextButton || !list) return;
    let position = 0;
    let maximumScroll = 0;
    let step = 0;
    let direction = 1;
    const updateControls = () => {
      position = Math.max(0, Math.min(position, maximumScroll));
      list.style.transform = `translateX(${-direction * position}px)`;
      previousButton.disabled = position <= 1;
      nextButton.disabled = position >= maximumScroll - 1;
    };

    const updateLayout = () => {
      // The drawer starts hidden. Never cache a zero-width card from that state.
      const width = track.clientWidth;
      if (!width) return;
      const columns = window.matchMedia('(min-width: 1024px)').matches ? 3 : 2;
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const cardSize = Math.max(1, (width - gap * (columns - 1)) / columns);
      list.style.setProperty('--cart-upsell-card-width', `${cardSize}px`);
      const count = list.querySelectorAll('.cart-drawer-upsell-slide').length;
      maximumScroll = Math.max(0, count * cardSize + Math.max(0, count - 1) * gap - width);
      step = columns * (cardSize + gap);
      direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      updateControls();
    };
    const controller = new AbortController();
    const listen = (element, event, callback, options = {}) =>
      element.addEventListener(event, callback, { ...options, signal: controller.signal });
    listen(previousButton, 'click', () => { position -= step; updateControls(); });
    listen(nextButton, 'click', () => { position += step; updateControls(); });

    let gesture = null;
    let suppressClick = false;
    listen(track, 'touchstart', (event) => {
      suppressClick = false;
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      gesture = { x: touch.clientX, y: touch.clientY, position, axis: null };
    }, { passive: true });
    listen(track, 'touchmove', (event) => {
      if (!gesture || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const dx = touch.clientX - gesture.x;
      const dy = touch.clientY - gesture.y;
      if (!gesture.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 8) {
        gesture.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (gesture.axis !== 'x') return;
      if (event.cancelable) event.preventDefault();
      suppressClick = true;
      track.classList.add('is-dragging');
      position = gesture.position - direction * dx;
      updateControls();
    }, { passive: false });
    const endGesture = () => {
      gesture = null;
      track.classList.remove('is-dragging');
    };
    ['touchend', 'touchcancel'].forEach((event) => listen(track, event, endGesture, { passive: true }));
    listen(track, 'click', (event) => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { capture: true });
    // Keyboard focus must reveal cards outside the clipped viewport too.
    listen(track, 'focusin', (event) => {
      const cards = [...list.querySelectorAll('.cart-drawer-upsell-slide')];
      const index = cards.indexOf(event.target.closest('.cart-drawer-upsell-slide'));
      if (index < 0) return;
      const size = parseFloat(list.style.getPropertyValue('--cart-upsell-card-width'));
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const start = index * (size + gap);
      if (start < position) position = start;
      else if (start + size > position + track.clientWidth) position = start + size - track.clientWidth;
      track.scrollLeft = 0;
      updateControls();
    });
    listen(window, 'resize', updateLayout, { passive: true });
    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(track);
    const listObserver = new MutationObserver(updateLayout);
    listObserver.observe(list, { childList: true });
    connectedSliders.set(slider, () => {
      resizeObserver.disconnect();
      listObserver.disconnect();
      controller.abort();
    });
    updateLayout();
  };

  const connectAllSliders = () => {
    connectedSliders.forEach((disconnect, slider) => {
      if (!slider.isConnected) {
        disconnect();
        connectedSliders.delete(slider);
      }
    });
    document.querySelectorAll('[data-cart-drawer-upsell-slider]').forEach(connectSlider);
  };

  // Cart section rendering replaces the slider without always emitting cart:updated.
  const observeCart = () => {
    if (!document.body) return;
    new MutationObserver((records) => {
      const containsSlider = (node) => node.nodeType === 1 && (
        node.matches('[data-cart-drawer-upsell-slider]') ||
        node.querySelector('[data-cart-drawer-upsell-slider]')
      );
      if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some(containsSlider))) {
        connectAllSliders();
      }
    }).observe(document.body, { childList: true, subtree: true });
    connectAllSliders();
  };

  document.addEventListener('DOMContentLoaded', observeCart, { once: true });
  document.addEventListener('shopify:section:load', connectAllSliders);
  document.addEventListener('cart:updated', connectAllSliders);
  if (document.readyState !== 'loading') observeCart();
})();
